import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { useTheme } from '../context/ThemeContext';
import {
  ChartBarIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { Line, Pie, Bar } from 'react-chartjs-2';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useAuth } from '../context/AuthContext';

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function getChartColors() {
  const isDark = document.documentElement.classList.contains('dark');
  return {
    grid: isDark ? '#444' : '#e5e7eb',
    text: isDark ? '#fff' : '#111',
    bg1: isDark ? 'rgba(37,99,235,0.7)' : 'rgba(37,99,235,0.2)',
    bg2: isDark ? 'rgba(16,185,129,0.7)' : 'rgba(16,185,129,0.2)',
    bg3: isDark ? 'rgba(239,68,68,0.7)' : 'rgba(239,68,68,0.2)',
    bg4: isDark ? 'rgba(251,191,36,0.7)' : 'rgba(251,191,36,0.2)',
    bg5: isDark ? 'rgba(168,85,247,0.7)' : 'rgba(168,85,247,0.2)',
    bg6: isDark ? 'rgba(34,197,94,0.7)' : 'rgba(34,197,94,0.2)',
    card: isDark ? 'bg-gray-800' : 'bg-white',
    border: isDark ? 'border-gray-700' : 'border-gray-200',
  };
}

const ModeratorPanel = () => {
  const { checkAuth } = useAuth();
  const { theme } = useTheme();
  // --- Дэшборд подопечных ---
  const [orgStats, setOrgStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  // --- Профиль пользователя с отчётами ---
  const [userProfile, setUserProfile] = useState(null);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [selectedDialogReport, setSelectedDialogReport] = useState(null);
  // --- Тариф организации ---
  const [availableTariffs, setAvailableTariffs] = useState([]);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [selectedTariffId, setSelectedTariffId] = useState(null);
  // --- Pending платежи ---
  const [pendingPayments, setPendingPayments] = useState([]);
  const [checkingPayment, setCheckingPayment] = useState(null);

  const fetchPendingPayments = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/payments/history?status=pending', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setPendingPayments(data.filter(p => p.status === 'pending'));
      }
    } catch (e) { console.error(e); }
  };

  const checkPayment = async (paymentId) => {
    setCheckingPayment(paymentId);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/payments/check/${paymentId}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'success') {
          toast.success('Платёж подтверждён!');
          fetchPendingPayments();
          fetchOrgStats();
          checkAuth(); // Обновляем глобальное состояние user/limits
        } else {
          toast.info(`Статус: ${data.status}`);
        }
      }
    } catch (e) { toast.error('Ошибка проверки'); }
    setCheckingPayment(null);
  };

  const fetchOrgStats = async (from, to) => {
    try {
      const token = localStorage.getItem('access_token');
      let url = '/api/admin/my-organization/stats';
      if (from && to) url += `?from=${from}&to=${to}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setOrgStats(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchUserStats = async (userId) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/admin/moderator/user/${userId}/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setUserStats(await res.json());
    } catch (e) { console.error(e); }
  };

  // Загрузка профиля пользователя с отчётами
  const fetchUserProfile = async (userId) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/admin/my-organization/user/${userId}/profile`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        setUserProfile(await res.json());
        setShowUserProfileModal(true);
      } else {
        toast.error('Не удалось загрузить профиль');
      }
    } catch (e) { toast.error('Ошибка загрузки профиля'); }
  };

  // Загрузка доступных тарифов
  const fetchTariffs = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/tariffs', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAvailableTariffs(await res.json());
    } catch (e) { console.error(e); }
  };

  // Удаление пользователя из организации
  const removeUserFromOrg = async (userId) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/admin/my-organization/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Пользователь удален из организации');
        fetchOrgStats(); // Обновляем список
      } else {
        toast.error('Ошибка удаления пользователя');
      }
    } catch (e) { 
      toast.error('Ошибка удаления пользователя'); 
    }
  };

  // Получение ожидающих платежей пользователя
  const getUserPendingPayments = async (userId) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/admin/my-organization/user/${userId}/pending-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const payments = await res.json();
        if (payments.length > 0) {
          const paymentsList = payments.map(p => 
            `${p.amount}₽ ${p.tariff_name ? `(${p.tariff_name})` : '(токены)'} - ${new Date(p.created_at).toLocaleDateString()}`
          ).join('\n');
          alert(`Ожидающие платежи:\n${paymentsList}`);
        } else {
          alert('Нет ожидающих платежей');
        }
      }
    } catch (e) {
      toast.error('Ошибка загрузки платежей');
    }
  };

  // Изменение тарифа организации
  const updateOrgTariff = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/admin/my-organization/tariff', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tariff_id: selectedTariffId })
      });
      if (res.ok) {
        toast.success('Тариф организации обновлён');
        setShowTariffModal(false);
        fetchOrgStats();
      } else {
        toast.error('Не удалось обновить тариф');
      }
    } catch (e) { toast.error('Ошибка обновления тарифа'); }
  };

  // --- Достижения ---
  const [achievements, setAchievements] = useState([]);
  const [loadingAchievements, setLoadingAchievements] = useState(true);
  const [errorAchievements, setErrorAchievements] = useState(null);
  const [showAddAchievementModal, setShowAddAchievementModal] = useState(false);
  const [achievementFormData, setAchievementFormData] = useState({
    title: '',
    description: '',
    icon: '',
    points: 0,
    is_repeatable: false,
    requirements: { type: 'none', value: '' }
  });
  const [showEditAchievementModal, setShowEditAchievementModal] = useState(false);
  const [currentAchievement, setCurrentAchievement] = useState({
    id: null,
    title: '',
    description: '',
    icon: '',
    points: 0,
    is_repeatable: false,
    requirements: { type: 'none', value: '' }
  });

  // --- Сценарии ---
  const [scenarios, setScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);
  const [errorScenarios, setErrorScenarios] = useState(null);
  const [showAddScenarioModal, setShowAddScenarioModal] = useState(false);
  const [orgTemplate, setOrgTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState([]);
  const [scenarioFormData, setScenarioFormData] = useState({
    name: '',
    description: '',
    variable_values: {},
    variable_options: {},
  });
  const [showEditScenarioModal, setShowEditScenarioModal] = useState(false);
  const [currentScenario, setCurrentScenario] = useState({
    id: null,
    name: '',
    description: '',
    variable_values: {},
    variable_options: {},
  });

  // --- Статистика и графики ---
  const [stats, setStats] = useState(null);
  const [topScenarios, setTopScenarios] = useState(null);
  const [achievementsDist, setAchievementsDist] = useState(null);
  const [collapseStats, setCollapseStats] = useState(false);
  const [collapseCharts, setCollapseCharts] = useState(false);
  const [errorStats, setErrorStats] = useState(null);
  const [achievementSearch, setAchievementSearch] = useState('');
  const [achievementSort, setAchievementSort] = useState('title');
  const [scenarioSearch, setScenarioSearch] = useState('');
  const [activityDays, setActivityDays] = useState(14);
  const [activityFrom, setActivityFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 13);
    return d.toISOString().split('T')[0];
  });
  const [activityTo, setActivityTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [scenarioSort, setScenarioSort] = useState('name');
  const [achievementPage, setAchievementPage] = useState(1);
  const achievementsPerPage = 5;
  const filteredAchievements = achievements
    .filter(a =>
      (a.title || '').toLowerCase().includes(achievementSearch.toLowerCase()) ||
      (a.description || '').toLowerCase().includes(achievementSearch.toLowerCase())
    )
    .sort((a, b) => {
      if (achievementSort === 'title') {
        return (a.title || '').localeCompare(b.title || '');
      }
      return 0;
    });
  const paginatedAchievements = filteredAchievements.slice((achievementPage-1)*achievementsPerPage, achievementPage*achievementsPerPage);
  const achievementTotalPages = Math.ceil(filteredAchievements.length / achievementsPerPage);

  const [scenarioPage, setScenarioPage] = useState(1);
  const scenariosPerPage = 5;
  const filteredScenarios = scenarios
    .filter(s =>
      (s.name || '').toLowerCase().includes(scenarioSearch.toLowerCase()) ||
      (s.description || '').toLowerCase().includes(scenarioSearch.toLowerCase())
    )
    .sort((a, b) => {
      if (scenarioSort === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      return 0;
    });
  const paginatedScenarios = filteredScenarios.slice((scenarioPage-1)*scenariosPerPage, scenarioPage*scenariosPerPage);
  const scenarioTotalPages = Math.ceil(filteredScenarios.length / scenariosPerPage);

  // --- Загрузка иконок ---
  const [availableIcons, setAvailableIcons] = useState([]);
  const [loadingIcons, setLoadingIcons] = useState(false);
  const fetchAvailableIcons = async () => {
    setLoadingIcons(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/upload/achievement_icons', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableIcons(data.icons || []);
      } else {
        setAvailableIcons([]);
      }
    } catch (e) {
      setAvailableIcons([]);
    } finally {
      setLoadingIcons(false);
    }
  };

  // --- Модалки ---
  const [confirmModal, setConfirmModal] = useState({ show: false, onConfirm: null, text: '' });
  const [showScenarioDetailsModal, setShowScenarioDetailsModal] = useState(false);

  // --- Инвайт-ссылки ---
  const [inviteLinks, setInviteLinks] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ max_uses: '', expires_days: '' });

  const fetchInviteLinks = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/admin/my-organization/invite-links', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setInviteLinks(await res.json());
    } catch (e) { console.error(e); }
  };

  const createInviteLink = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const body = {};
      if (inviteForm.max_uses) body.max_uses = parseInt(inviteForm.max_uses);
      if (inviteForm.expires_days) body.expires_days = parseInt(inviteForm.expires_days);
      const res = await fetch('/api/admin/my-organization/invite-links', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Ссылка создана');
        navigator.clipboard.writeText(data.url);
        toast.info('Ссылка скопирована в буфер');
        setShowInviteModal(false);
        setInviteForm({ max_uses: '', expires_days: '' });
        fetchInviteLinks();
      } else {
        toast.error('Ошибка создания ссылки');
      }
    } catch (e) { toast.error('Ошибка'); }
  };

  const deleteInviteLink = async (id) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/admin/my-organization/invite-links/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Ссылка деактивирована');
        fetchInviteLinks();
      }
    } catch (e) { toast.error('Ошибка'); }
  };

  // --- Заявки на вступление ---
  const [joinRequests, setJoinRequests] = useState([]);

  const fetchJoinRequests = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/admin/my-organization/join-requests', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setJoinRequests(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleJoinRequest = async (requestId, action) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/admin/my-organization/join-requests/${requestId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(action === 'approve' ? 'Заявка одобрена' : 'Заявка отклонена');
        fetchJoinRequests();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Ошибка');
      }
    } catch (e) { toast.error('Ошибка'); }
  };

  // --- useEffect для загрузки данных ---
  useEffect(() => {
    const fetchAchievements = async () => {
      setLoadingAchievements(true);
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch('/api/admin/my-organization/achievements', { headers: { 'Authorization': `Bearer ${token}` } });
        if (!response.ok) throw new Error('Ошибка загрузки достижений');
        const data = await response.json();
        setAchievements(data.map(a => ({ ...a, title: a.name })));
        setErrorAchievements(null);
      } catch (e) {
        setErrorAchievements('Ошибка загрузки достижений');
      } finally {
        setLoadingAchievements(false);
      }
    };
    const fetchScenarios = async () => {
      setLoadingScenarios(true);
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch('/api/admin/my-organization/scenarios', { headers: { 'Authorization': `Bearer ${token}` } });
        if (!response.ok) throw new Error('Ошибка загрузки сценариев');
        setScenarios(await response.json());
        setErrorScenarios(null);
      } catch (e) {
        setErrorScenarios('Ошибка загрузки сценариев');
      } finally {
        setLoadingScenarios(false);
      }
    };
    const fetchStats = async () => {
      const token = localStorage.getItem('access_token');
      try {
        const response = await fetch('/api/admin/stats', { headers: { 'Authorization': `Bearer ${token}` } });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
          setErrorStats(null);
        } else {
          setErrorStats('Не удалось загрузить статистику.');
        }
      } catch (e) {
        setErrorStats('Не удалось загрузить статистику. Попробуйте позже.');
      }
    };
    const fetchTopScenarios = async () => {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/admin/stats/top-scenarios', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setTopScenarios(await response.json());
    };
    const fetchAchievementsDist = async () => {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/admin/stats/achievements-distribution', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setAchievementsDist(await response.json());
    };
    fetchAchievements();
    fetchScenarios();
    fetchStats();
    fetchTopScenarios();
    fetchAchievementsDist();
    fetchAvailableIcons();
    fetchOrgStats();
    fetchTariffs();
    fetchOrgTemplate();
    fetchInviteLinks();
    fetchJoinRequests();
    fetchPendingPayments();
  }, []);

  // --- Обработчики для достижений ---
  const handleAddAchievementChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'requirementType' || name === 'requirementValue') {
      setAchievementFormData(prevData => ({
        ...prevData,
        requirements: {
          ...prevData.requirements,
          [name.replace('requirement', '').toLowerCase()]: value
        }
      }));
    } else {
      setAchievementFormData(prevData => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleEditAchievementChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'requirementType' || name === 'requirementValue') {
      setCurrentAchievement(prevData => ({
        ...prevData,
        requirements: {
          ...prevData.requirements,
          [name.replace('requirement', '').toLowerCase()]: value
        }
      }));
    } else {
      setCurrentAchievement(prevData => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleIconUpload = async (event, isEditForm = false) => {
    const file = event.target.files[0];
    if (!file) return;
    // Локальный preview (опционально)
    // ... можно добавить превью ...
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Токен авторизации не найден.');
      const response = await fetch('/api/upload/achievement_icon', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось загрузить иконку.');
      }
      const data = await response.json();
      const newIconUrl = data.url;
      if (isEditForm) {
        setCurrentAchievement(prevData => ({ ...prevData, icon: newIconUrl }));
      } else {
        setAchievementFormData(prevData => ({ ...prevData, icon: newIconUrl }));
      }
      toast.success('Иконка успешно загружена!');
    } catch (err) {
      toast.error(`Ошибка при загрузке иконки: ${err.message}`);
    }
  };

  const handleEditAchievementClick = (achievement) => {
    let parsedRequirements = { type: 'none', value: '' };
    if (achievement.requirements) {
      try {
        const req = typeof achievement.requirements === 'string' ? JSON.parse(achievement.requirements) : achievement.requirements;
        if (req && req.type && req.value !== undefined) {
          parsedRequirements = { type: req.type, value: String(req.value) };
        }
      } catch (e) {}
    }
    setCurrentAchievement({
      id: achievement.id,
      title: achievement.title,
      description: achievement.description,
      icon: achievement.icon || '',
      points: achievement.points || 0,
      is_repeatable: achievement.is_repeatable || false,
      requirements: parsedRequirements
    });
    setShowEditAchievementModal(true);
  };

  const handleSaveAchievementEdit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Токен авторизации не найден.');
      setLoadingAchievements(true);
      const response = await fetch(`/api/admin/my-organization/achievements/${currentAchievement.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: currentAchievement.title,
          description: currentAchievement.description,
          icon: currentAchievement.icon,
          points: parseInt(currentAchievement.points, 10),
          is_repeatable: currentAchievement.is_repeatable,
          requirements: currentAchievement.requirements.type !== 'none' ? currentAchievement.requirements : {}
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось обновить достижение.');
      }
      toast.success('Достижение успешно обновлено!');
      setShowEditAchievementModal(false);
      // обновить список достижений
      const achievementsRes = await fetch('/api/admin/my-organization/achievements', { headers: { 'Authorization': `Bearer ${token}` } });
      setAchievements(await achievementsRes.json());
    } catch (err) {
      setErrorAchievements(err.message);
      toast.error(`Ошибка: ${err.message}`);
    } finally {
      setLoadingAchievements(false);
    }
  };

  const handleAddAchievementSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Токен авторизации не найден.');
      setLoadingAchievements(true);
      const response = await fetch('/api/admin/my-organization/achievements', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...achievementFormData,
          points: parseInt(achievementFormData.points, 10),
          is_repeatable: achievementFormData.is_repeatable,
          requirements: achievementFormData.requirements.type !== 'none' ? achievementFormData.requirements : {}
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось добавить достижение.');
      }
      toast.success('Достижение успешно добавлено!');
      setShowAddAchievementModal(false);
      setAchievementFormData({ title: '', description: '', icon: '', points: 0, is_repeatable: false, requirements: { type: 'none', value: '' } });
      // обновить список достижений
      const achievementsRes = await fetch('/api/admin/my-organization/achievements', { headers: { 'Authorization': `Bearer ${token}` } });
      setAchievements(await achievementsRes.json());
    } catch (err) {
      setErrorAchievements(err.message);
      toast.error(`Ошибка: ${err.message}`);
    } finally {
      setLoadingAchievements(false);
    }
  };

  const handleDeleteAchievement = async (achievementId) => {
    setConfirmModal({
      show: true,
      text: 'Вы уверены, что хотите удалить это достижение?',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('access_token');
          if (!token) throw new Error('Токен авторизации не найден.');
          setLoadingAchievements(true);
          const response = await fetch(`/api/admin/my-organization/achievements/${achievementId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Не удалось удалить достижение.');
          }
          toast.success('Достижение успешно удалено!');
          // обновить список достижений
          const achievementsRes = await fetch('/api/admin/my-organization/achievements', { headers: { 'Authorization': `Bearer ${token}` } });
          setAchievements(await achievementsRes.json());
        } catch (err) {
          setErrorAchievements(err.message);
          toast.error(`Ошибка: ${err.message}`);
        } finally {
          setLoadingAchievements(false);
          setConfirmModal({ show: false, onConfirm: null, text: '' });
        }
      }
    });
  };

  // --- Обработчики для сценариев ---
  const handleAddScenarioSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Токен авторизации не найден.');
      setLoadingScenarios(true);
      console.log('Отправка сценария:', scenarioFormData);
      const response = await fetch('/api/admin/my-organization/scenarios', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(scenarioFormData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось добавить сценарий.');
      }
      toast.success('Сценарий успешно добавлен!');
      setShowAddScenarioModal(false);
      setScenarioFormData({ name: '', description: '', variable_values: {}, variable_options: {} });
      // обновить список сценариев
      const scenariosRes = await fetch('/api/admin/my-organization/scenarios', { headers: { 'Authorization': `Bearer ${token}` } });
      setScenarios(await scenariosRes.json());
    } catch (err) {
      setErrorScenarios(err.message);
      toast.error(`Ошибка: ${err.message}`);
    } finally {
      setLoadingScenarios(false);
    }
  };

  // Загрузка шаблона организации
  const fetchOrgTemplate = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/admin/my-organization/template', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        console.log('Шаблон организации:', data);
        setOrgTemplate(data.template);
        setTemplateVariables(data.variables || []);
      }
    } catch (e) { console.error(e); }
  };

  const handleEditScenarioClick = (scenario) => {
    console.log('Редактирование сценария:', scenario);
    setCurrentScenario({ id: scenario.id, name: scenario.name, description: scenario.description, variable_values: scenario.variable_values || {}, variable_options: scenario.variable_options || {} });
    setShowEditScenarioModal(true);
  };

  const handleSaveScenarioEdit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Токен авторизации не найден.');
      setLoadingScenarios(true);
      const response = await fetch(`/api/admin/my-organization/scenarios/${currentScenario.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(currentScenario),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось обновить сценарий.');
      }
      toast.success('Сценарий успешно обновлён!');
      setShowEditScenarioModal(false);
      // обновить список сценариев
      const scenariosRes = await fetch('/api/admin/my-organization/scenarios', { headers: { 'Authorization': `Bearer ${token}` } });
      setScenarios(await scenariosRes.json());
    } catch (err) {
      setErrorScenarios(err.message);
      toast.error(`Ошибка: ${err.message}`);
    } finally {
      setLoadingScenarios(false);
    }
  };

  const handleDeleteScenario = async (scenarioId) => {
    setConfirmModal({
      show: true,
      text: 'Вы уверены, что хотите удалить этот сценарий?',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('access_token');
          if (!token) throw new Error('Токен авторизации не найден.');
          setLoadingScenarios(true);
          const response = await fetch(`/api/admin/my-organization/scenarios/${scenarioId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Не удалось удалить сценарий.');
          }
          toast.success('Сценарий успешно удалён!');
          // обновить список сценариев
          const scenariosRes = await fetch('/api/admin/my-organization/scenarios', { headers: { 'Authorization': `Bearer ${token}` } });
          setScenarios(await scenariosRes.json());
        } catch (err) {
          setErrorScenarios(err.message);
          toast.error(`Ошибка: ${err.message}`);
        } finally {
          setLoadingScenarios(false);
          setConfirmModal({ show: false, onConfirm: null, text: '' });
        }
      }
    });
  };

  // --- JSX рендер: полностью копирую layout и стили из Admin.jsx, ---
  return (
    <div className="min-h-screen bg-blue-50 dark:bg-gradient-to-br dark:from-gray-900 dark:to-gray-950 py-6 sm:py-12 flex flex-col items-center transition-all overflow-x-hidden">
      {/* Унификация стилей для секций */}
      <style>{`
        .panel-section {
          background: var(--tw-bg-opacity,1) #fff;
          border-radius: 18px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.07);
          border: 1.5px solid #e5e7eb;
          padding: 18px 10px 18px 10px;
          margin-bottom: 18px;
          transition: box-shadow 0.2s, border 0.2s, background 0.2s;
          max-width: 100%;
          overflow-x: auto;
        }
        .dark .panel-section {
          background: #181f2a;
          border-color: #374151;
        }
        .panel-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
        }
        .panel-title {
          font-size: 1.25rem;
          font-weight: 800;
          color: #0d47a1;
          letter-spacing: -0.5px;
          line-height: 1.1;
        }
        .dark .panel-title {
          color: #b3cfff;
        }
        .panel-icon {
          width: 32px;
          height: 32px;
          color: #2563eb;
        }
        .dark .panel-icon {
          color: #60a5fa;
        }
        .panel-btn {
          border-radius: 10px;
          font-weight: 700;
          font-size: 15px;
          padding: 14px 0;
          min-width: 120px;
          min-height: 44px;
          transition: background 0.18s, color 0.18s, box-shadow 0.18s;
          box-shadow: 0 1px 4px rgba(0,0,0,0.07);
          outline: none;
        }
        .panel-btn:active {
          box-shadow: 0 1px 2px rgba(0,0,0,0.10);
        }
        .panel-btn-primary {
          background: #0ea5e9;
          color: #fff;
        }
        .panel-btn-primary:hover {
          background: #0369a1;
        }
        .panel-btn-secondary {
          background: #e0e7ef;
          color: #0d47a1;
        }
        .panel-btn-secondary:hover {
          background: #b3cfff;
        }
        .dark .panel-btn-secondary {
          background: #232b3a;
          color: #b3cfff;
        }
        .panel-table {
          border-radius: 12px;
          overflow: hidden;
          width: 100%;
          margin-bottom: 8px;
          font-size: 15px;
          line-height: 1.4;
          background: #fff;
          color: #1a202c;
        }
        .dark .panel-table {
          background: #181f2a;
          color: #f3f4f6;
        }
        .panel-table th, .panel-table td {
          padding: 13px 14px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 140px;
        }
        .panel-table th {
          font-weight: 700;
          background: #f1f8ff;
          color: #0d47a1;
        }
        .dark .panel-table th {
          background: #232b3a;
          color: #b3cfff;
        }
        @media (max-width: 640px) {
          .panel-section { padding: 10px 2px 10px 2px; border-radius: 12px; }
          .panel-title { font-size: 1.1rem; }
          .panel-table th, .panel-table td { padding: 9px 7px; font-size: 13.5px; max-width: 90px; }
          .panel-btn { font-size: 14px !important; padding: 12px 0 !important; min-width: 90px !important; min-height: 40px !important; }
        }
      `}</style>
    
      {/* Хлебные крошки */}
      <nav className="w-full max-w-[1200px] mb-1 sm:mb-4 text-xs sm:text-sm">
        <ol className="flex flex-wrap items-center space-x-1 sm:space-x-2 text-gray-500 dark:text-gray-400 break-words">
          <li>
            <a href="/" className="hover:underline text-blue-600 dark:text-blue-400">Главная</a>
          </li>
          <li className="text-gray-400 dark:text-gray-500">/</li>
          <li className="text-gray-700 dark:text-gray-200 font-semibold">Модератор-панель</li>
        </ol>
      </nav>
      {/* Крупный заголовок */}
      <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-[48px] font-bold text-gray-900 dark:text-white mb-1 sm:mb-4 text-center font-['Inter'] drop-shadow-lg transition-colors break-words leading-tight">Модератор-панель</h1>
      <p className="text-xs sm:text-base md:text-lg text-gray-700 dark:text-gray-300 mb-3 sm:mb-10 text-center break-words">Управление достижениями, сценариями и аналитикой</p>
      <div className="w-full max-w-full md:max-w-[900px] flex flex-col gap-3 sm:gap-8 mb-4 sm:mb-12 px-0.5 sm:px-2 md:px-0">
        
        {/* Дэшборд подопечных */}
        {orgStats && (
          <div className="panel-section">
            <div className="panel-header">
              <UserGroupIcon className="panel-icon w-6 h-6 sm:w-12 sm:h-12 text-blue-800 dark:text-blue-300" />
              <span className="panel-title text-base sm:text-3xl font-extrabold text-blue-900 dark:text-blue-200">Мои подопечные</span>
            </div>
            {/* Информация об организации */}
            {orgStats.organization && (
              <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-700">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="text-lg font-bold text-blue-900 dark:text-blue-200">{orgStats.organization.name}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Тариф: <span className="font-medium">{orgStats.organization.tariff_name || 'Не назначен'}</span>
                      {orgStats.organization.tariff_expires_at && (
                        <span className="ml-2 text-blue-600 dark:text-blue-400">
                          (до {new Date(orgStats.organization.tariff_expires_at).toLocaleDateString('ru')} • {(() => {
                            const end = new Date(orgStats.organization.tariff_expires_at);
                            const now = new Date();
                            end.setHours(0,0,0,0);
                            now.setHours(0,0,0,0);
                            const days = Math.round((end - now) / (1000 * 60 * 60 * 24));
                            return days > 0 ? `осталось ${days} дн.` : 'истёк';
                          })()})
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => setShowTariffModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                    Купить тариф
                  </button>
                </div>
                {orgStats.organization.tariff && (
                  <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                    <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300 mb-3">
                      <li>• {orgStats.organization.tariff.monthly_limit > 0 ? `${orgStats.organization.tariff.monthly_limit.toLocaleString()} токенов` : 'Безлимитное общение'}</li>
                      {orgStats.organization.tariff.daily_limit > 0 && <li>• До {orgStats.organization.tariff.daily_limit.toLocaleString()} токенов в день</li>}
                      <li>• Срок действия: {orgStats.organization.tariff.period_days || 30} дней</li>
                      {orgStats.organization.tariff.participant_limit && <li>• До {orgStats.organization.tariff.participant_limit} пользователей</li>}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {/* Pending платежи */}
            {pendingPayments.length > 0 && (
              <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-300 dark:border-yellow-700">
                <div className="font-medium text-yellow-800 dark:text-yellow-300 mb-2">⏳ Ожидающие платежи</div>
                {pendingPayments.map(p => (
                  <div key={p.id} className="flex justify-between items-center py-2 border-b border-yellow-200 dark:border-yellow-800 last:border-0">
                    <div className="text-sm">
                      <span className="font-medium">{p.amount?.toLocaleString()} ₽</span>
                      {p.tariff_name && <span className="text-gray-600 dark:text-gray-400"> • {p.tariff_name}</span>}
                      <span className="text-gray-500 text-xs ml-2">{new Date(p.created_at).toLocaleString('ru')}</span>
                    </div>
                    <button onClick={() => checkPayment(p.id)} disabled={checkingPayment === p.id} className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-sm disabled:opacity-50">
                      {checkingPayment === p.id ? '...' : 'Проверить'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-800">
                <div className="text-2xl font-bold text-blue-600">{orgStats.users_count}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Участников</div>
              </div>
              <div className="text-center p-3 bg-green-50 dark:bg-green-900/30 rounded-xl border border-green-200 dark:border-green-800">
                <div className="text-2xl font-bold text-green-600">{orgStats.completed_dialogs}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Завершено</div>
              </div>
              <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/30 rounded-xl border border-purple-200 dark:border-purple-800">
                <div className="text-2xl font-bold text-purple-600">{orgStats.total_dialogs}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Всего диалогов</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white dark:bg-gray-800 rounded-xl text-xs sm:text-sm transition-colors">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Пользователь</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Тренировок</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Ср. оценка</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Активность</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Платежи</th>
                    <th className="px-3 py-3 text-center font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {(orgStats.users || []).map(u => (
                    <tr key={u.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-3 py-3 text-gray-900 dark:text-gray-100 border-t border-gray-200 dark:border-gray-700 cursor-pointer" onClick={() => { setSelectedUser(u); fetchUserStats(u.id); }}>{u.username || u.email}</td>
                      <td className="px-3 py-3 text-center text-gray-900 dark:text-gray-100 border-t border-gray-200 dark:border-gray-700 cursor-pointer" onClick={() => { setSelectedUser(u); fetchUserStats(u.id); }}>{u.completed_count}/{u.dialogs_count}</td>
                      <td className="px-3 py-3 text-center text-gray-900 dark:text-gray-100 border-t border-gray-200 dark:border-gray-700 cursor-pointer" onClick={() => { setSelectedUser(u); fetchUserStats(u.id); }}>{u.avg_score ? `${u.avg_score}/10` : '—'}</td>
                      <td className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 cursor-pointer" onClick={() => { setSelectedUser(u); fetchUserStats(u.id); }}>{u.last_activity ? new Date(u.last_activity).toLocaleDateString('ru') : '—'}</td>
                      <td className="px-3 py-3 text-center border-t border-gray-200 dark:border-gray-700">
                        {u.pending_payments > 0 ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              getUserPendingPayments(u.id);
                            }}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-800 cursor-pointer"
                            title="Показать ожидающие платежи"
                          >
                            {u.pending_payments} ожидает
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center border-t border-gray-200 dark:border-gray-700">
                        {u.role !== 'moderator' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Удалить ${u.username || u.email} из организации?`)) {
                                removeUserFromOrg(u.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            title="Удалить из организации"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Модалка детальной статистики пользователя */}
        {selectedUser && userStats && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{userStats.user?.username || userStats.user?.email}</h3>
                <button onClick={() => { setSelectedUser(null); setUserStats(null); }} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-2xl">&times;</button>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                  <div className="font-bold text-gray-900 dark:text-white">{userStats.total_dialogs}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Всего</div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                  <div className="font-bold text-gray-900 dark:text-white">{userStats.completed_dialogs}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Завершено</div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                  <div className="font-bold text-gray-900 dark:text-white">{userStats.avg_score || '—'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Ср. оценка</div>
                </div>
              </div>
              <h4 className="font-medium mb-2 text-gray-900 dark:text-white">Последние тренировки:</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(userStats.recent_dialogs || []).map(d => (
                  <div key={d.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-xl text-sm border border-gray-200 dark:border-gray-600">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">{d.scenario_name || 'Сценарий'}</span>
                      <span className={d.status === 'completed' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}>{d.status === 'completed' ? '✓' : '...'}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{d.started_at ? new Date(d.started_at).toLocaleDateString('ru') : ''} {d.score ? `• ${d.score}/10` : ''}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => { fetchUserProfile(selectedUser.id); setSelectedUser(null); setUserStats(null); }} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-xl font-semibold transition-colors">Профиль и отчёты</button>
                <button onClick={() => { setSelectedUser(null); setUserStats(null); }} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-xl font-semibold transition-colors">Закрыть</button>
              </div>
            </div>
          </div>
        )}

        {/* Модалка профиля пользователя с отчётами */}
        {showUserProfileModal && userProfile && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Профиль: {userProfile.user?.username || userProfile.user?.email}</h3>
                <button onClick={() => { setShowUserProfileModal(false); setUserProfile(null); setSelectedDialogReport(null); }} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-2xl">&times;</button>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Email</div>
                  <div className="font-medium text-gray-900 dark:text-white">{userProfile.user?.email}</div>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-xl">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Баланс токенов</div>
                  <div className="font-medium text-gray-900 dark:text-white">{userProfile.user?.tokens_balance || 0}</div>
                </div>
                <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-xl">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Тариф</div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {userProfile.user?.tariff_name || 'Организации'}
                    {userProfile.user?.tariff_expires_at && (
                      <span className="block text-xs text-purple-600 dark:text-purple-400 mt-1">
                        до {new Date(userProfile.user.tariff_expires_at).toLocaleDateString('ru')} ({(() => {
                          const end = new Date(userProfile.user.tariff_expires_at);
                          const now = new Date();
                          end.setHours(0,0,0,0);
                          now.setHours(0,0,0,0);
                          const days = Math.round((end - now) / (1000 * 60 * 60 * 24));
                          return days > 0 ? `${days} дн.` : 'истёк';
                        })()})
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded-xl">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Роль</div>
                  <div className="font-medium text-gray-900 dark:text-white">{userProfile.user?.role || 'user'}</div>
                </div>
              </div>

              <h4 className="font-semibold mb-3 text-gray-900 dark:text-white">Тренировки и отчёты ({userProfile.total_dialogs})</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {(userProfile.dialogs || []).map(d => (
                  <div key={d.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{d.scenario_name || 'Сценарий'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {d.started_at ? new Date(d.started_at).toLocaleString('ru') : '—'}
                          {d.duration ? ` • ${Math.floor(d.duration / 60)} мин` : ''}
                          {d.score ? ` • Оценка: ${d.score}/10` : ''}
                        </div>
                      </div>
                      {d.analysis && (
                        <button 
                          onClick={() => setSelectedDialogReport(selectedDialogReport === d.id ? null : d.id)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 text-sm font-medium"
                        >
                          {selectedDialogReport === d.id ? 'Скрыть отчёт' : 'Показать отчёт'}
                        </button>
                      )}
                    </div>
                    {selectedDialogReport === d.id && d.analysis && (
                      <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none">
                        <MarkdownRenderer variant="report" darkMode={theme === 'dark'}>{d.analysis}</MarkdownRenderer>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <button onClick={() => { setShowUserProfileModal(false); setUserProfile(null); setSelectedDialogReport(null); }} className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-xl font-semibold transition-colors">Закрыть</button>
            </div>
          </div>
        )}

        {/* Карточка достижений */}
        <div className="panel-section">
          <div className="panel-header">
            <ChartBarIcon className="panel-icon w-6 h-6 sm:w-12 sm:h-12 text-blue-800 dark:text-blue-300 mr-0 sm:mr-4" />
            <span className="panel-title text-base sm:text-3xl font-extrabold text-blue-900 dark:text-blue-200">Достижения</span>
          </div>
          {/* Поиск и сортировка */}
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center mb-2 sm:mb-4 gap-1 sm:gap-0">
              <input
                type="text"
                placeholder="Поиск по названию или описанию"
              className="border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 w-full sm:w-64 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400 transition-colors text-xs sm:text-base"
                value={achievementSearch}
                onChange={e => setAchievementSearch(e.target.value)}
              />
              <button
              className={`px-3 py-1.5 rounded-lg font-semibold transition ${achievementSort === 'title' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'} hover:bg-blue-700 text-xs sm:text-base`}
                onClick={() => setAchievementSort('title')}
              style={{ minWidth: '90px' }}
              >
                Сортировать по названию
              </button>
              <button 
                onClick={() => setShowAddAchievementModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-3 py-2 hover:bg-green-700 transition min-w-[90px] text-xs sm:text-base"
              >
                Добавить новое достижение
              </button>
          </div>
          {/* Таблица достижений */}
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 mb-2">
            <table className="min-w-full bg-white dark:bg-gray-800 rounded-xl text-xs sm:text-sm transition-colors">
              <thead>
                <tr className="bg-blue-50 dark:bg-gray-700 text-blue-800 dark:text-blue-200 text-left">
                  <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Название</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Описание</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 hidden xs:table-cell">Требование</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Действия</th>
                </tr>
              </thead>
              <tbody>
                {loadingAchievements ? (
                  <tr><td colSpan="4" className="px-4 py-2 text-center">Загрузка достижений...</td></tr>
                ) : errorAchievements ? (
                  <tr><td colSpan="4" className="px-4 py-2 text-center text-red-600">Ошибка: {errorAchievements}</td></tr>
                ) : achievements.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-2 text-center">Нет доступных достижений.</td></tr>
                ) : (
                  paginatedAchievements.map((achievement, idx) => {
                    let req = achievement.requirements;
                    if (typeof req === 'string') {
                      try { req = JSON.parse(req); } catch { req = {}; }
                    }
                    let reqText = 'Нет требования';
                    if (req && req.type === 'total_dialogs') reqText = `Завершить ${req.value} диалогов`;
                    else if (req && req.type === 'time') reqText = `Потратить ${req.value} секунд`;
                    return (
                      <tr
                        key={achievement.id}
                        className={`border-t border-gray-200 dark:border-gray-700 transition ${idx % 2 === 0 ? 'bg-white dark:bg-gray-900/80' : 'bg-blue-50 dark:bg-gray-900/60'} hover:bg-blue-50 dark:hover:bg-gray-800/70`}
                      >
                        <td className="px-3 py-3 text-gray-900 dark:text-gray-100 border-t border-gray-200 dark:border-gray-700">{achievement.title}</td>
                        <td className="px-3 py-3 text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700">{achievement.description}</td>
                        <td className="px-3 py-3 text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 hidden xs:table-cell">{reqText}</td>
                        <td className="px-3 py-3 text-gray-900 dark:text-gray-100 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex gap-2 items-center">
                            <button 
                              onClick={() => handleEditAchievementClick(achievement)}
                              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1 transition flex items-center"
                            >
                              <PencilIcon className="w-4 h-4 mr-1" />
                              Редактировать
                            </button>
                            <button 
                              onClick={() => handleDeleteAchievement(achievement.id)}
                              className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1 transition flex items-center"
                            >
                              <TrashIcon className="w-4 h-4 mr-1" />
                              Удалить
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Пагинация */}
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setAchievementPage(p => Math.max(1, p-1))} disabled={achievementPage===1} className="px-3 py-1 rounded bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50">Назад</button>
            {[...Array(achievementTotalPages)].map((_,i) => (
              <button key={i} onClick={()=>setAchievementPage(i+1)} className={`px-3 py-1 rounded ${achievementPage===i+1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}>{i+1}</button>
            ))}
            <button onClick={() => setAchievementPage(p => Math.min(achievementTotalPages, p+1))} disabled={achievementPage===achievementTotalPages} className="px-3 py-1 rounded bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50">Вперёд</button>
          </div>
        </div>

        {/* Карточка инвайт-ссылок */}
        <div className="panel-section bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <UserGroupIcon className="w-8 h-8 text-green-600 dark:text-green-400 mr-3" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">Инвайт-ссылки</span>
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition"
            >
              + Создать ссылку
            </button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Создайте ссылку для приглашения участников в вашу организацию
          </p>
          {inviteLinks.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">Нет активных ссылок</p>
          ) : (
            <div className="space-y-3">
              {inviteLinks.filter(l => l.is_active).map(link => (
                <div key={link.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <code className="text-sm bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">{link.url}</code>
                      <button
                        onClick={() => { navigator.clipboard.writeText(link.url); toast.success('Скопировано'); }}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        📋
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Использований: {link.uses_count}{link.max_uses ? ` / ${link.max_uses}` : ' (безлимит)'}
                      {link.expires_at && ` • Истекает: ${new Date(link.expires_at).toLocaleDateString('ru')}`}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteInviteLink(link.id)}
                    className="text-red-600 hover:text-red-800 text-sm px-3 py-1 border border-red-300 rounded"
                  >
                    Деактивировать
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Карточка заявок на вступление */}
        <div className="panel-section bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-6 mb-6">
          <div className="flex items-center mb-4">
            <UserGroupIcon className="w-8 h-8 text-orange-600 dark:text-orange-400 mr-3" />
            <span className="text-xl font-bold text-gray-900 dark:text-white">Заявки на вступление</span>
            {joinRequests.filter(r => r.status === 'pending').length > 0 && (
              <span className="ml-2 px-2 py-1 bg-orange-500 text-white text-xs rounded-full">
                {joinRequests.filter(r => r.status === 'pending').length}
              </span>
            )}
          </div>
          {joinRequests.filter(r => r.status === 'pending').length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">Нет новых заявок</p>
          ) : (
            <div className="space-y-3">
              {joinRequests.filter(r => r.status === 'pending').map(req => (
                <div key={req.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{req.user_name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{req.user_email}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Подана: {new Date(req.created_at).toLocaleString('ru')}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleJoinRequest(req.id, 'approve')}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                    >
                      ✓ Принять
                    </button>
                    <button
                      onClick={() => handleJoinRequest(req.id, 'reject')}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
                    >
                      ✗ Отклонить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* История обработанных заявок */}
          {joinRequests.filter(r => r.status !== 'pending').length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700">
                История заявок ({joinRequests.filter(r => r.status !== 'pending').length})
              </summary>
              <div className="mt-2 space-y-2">
                {joinRequests.filter(r => r.status !== 'pending').slice(0, 10).map(req => (
                  <div key={req.id} className="p-2 bg-gray-100 dark:bg-gray-600 rounded-lg text-sm flex justify-between">
                    <span>{req.user_name}</span>
                    <span className={req.status === 'approved' ? 'text-green-600' : 'text-red-600'}>
                      {req.status === 'approved' ? '✓ Принята' : '✗ Отклонена'}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Карточка сценариев */}
        <div className="panel-section">
          <div className="panel-header">
            <ChartBarIcon className="panel-icon w-12 h-12 text-blue-800 dark:text-blue-300 mr-4" />
            <span className="panel-title text-3xl font-extrabold text-blue-900 dark:text-blue-200">Сценарии</span>
          </div>
          {/* Поиск и сортировка */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <input
                type="text"
                placeholder="Поиск по названию или описанию"
                className="border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-2 text-base focus:outline-none focus:border-blue-800 dark:focus:border-blue-400 w-full sm:w-64 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                value={scenarioSearch}
                onChange={e => setScenarioSearch(e.target.value)}
              />
              <button
                className={`px-5 py-2 rounded-lg font-semibold transition ${scenarioSort === 'name' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'} hover:bg-blue-700`}
                onClick={() => setScenarioSort('name')}
                style={{ minWidth: '180px' }}
              >
                Сортировать по названию
              </button>
              <button 
                onClick={() => setShowAddScenarioModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 hover:bg-green-700 transition min-w-[180px]"
              >
                Добавить новый сценарий
              </button>
            </div>
          </div>
          {/* Таблица сценариев */}
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 mb-4">
            <table className="min-w-full bg-white dark:bg-gray-800 rounded-xl text-xs sm:text-sm transition-colors">
              <thead>
                <tr className="bg-blue-50 dark:bg-gray-700 text-blue-800 dark:text-blue-200 text-left">
                  <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Название</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 hidden sm:table-cell">Описание</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 hidden md:table-cell">Роль</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Действия</th>
                </tr>
              </thead>
              <tbody>
                {loadingScenarios ? (
                  <tr><td colSpan="4" className="px-4 py-2 text-center">Загрузка сценариев...</td></tr>
                ) : errorScenarios ? (
                  <tr><td colSpan="4" className="px-4 py-2 text-center text-red-600">Ошибка: {errorScenarios}</td></tr>
                ) : scenarios.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-2 text-center text-gray-400 dark:text-gray-500">Нет доступных сценариев.</td></tr>
                ) : (
                  paginatedScenarios.map((scenario, idx) => (
                    <tr
                      key={scenario.id}
                      className={`border-t border-gray-200 dark:border-gray-700 transition ${idx % 2 === 0 ? 'bg-white dark:bg-gray-900/80' : 'bg-blue-50 dark:bg-gray-900/60'} hover:bg-blue-50 dark:hover:bg-gray-800/70`}
                    >
                      <td className="px-3 py-3 text-gray-900 dark:text-gray-100 border-t border-gray-200 dark:border-gray-700">{scenario.name}</td>
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 hidden sm:table-cell">{scenario.description || '-'}</td>
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 hidden md:table-cell">{(scenario.variable_values || {}).role || '-'}</td>
                      <td className="px-3 py-3 text-gray-900 dark:text-gray-100 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex gap-2 items-center">
                          <button 
                            onClick={() => handleEditScenarioClick(scenario)}
                            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1 transition flex items-center"
                          >
                            <PencilIcon className="w-4 h-4 mr-1" />
                            Редактировать
                          </button>
                          <button 
                            onClick={() => handleDeleteScenario(scenario.id)}
                            className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1 transition flex items-center"
                          >
                            <TrashIcon className="w-4 h-4 mr-1" />
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Пагинация */}
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setScenarioPage(p => Math.max(1, p-1))} disabled={scenarioPage===1} className="px-3 py-1 rounded bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50">Назад</button>
            {[...Array(scenarioTotalPages)].map((_,i) => (
              <button key={i} onClick={()=>setScenarioPage(i+1)} className={`px-3 py-1 rounded ${scenarioPage===i+1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}>{i+1}</button>
            ))}
            <button onClick={() => setScenarioPage(p => Math.min(scenarioTotalPages, p+1))} disabled={scenarioPage===scenarioTotalPages} className="px-3 py-1 rounded bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50">Вперёд</button>
          </div>
        </div>
      </div>
      {/* Блок статистики и графиков */}
      <div id="moderator-stats" className="w-full max-w-[900px] mt-4 md:mt-6 px-2 sm:px-4 md:px-8">
        <h2 className="text-3xl font-bold mb-6 text-center text-gray-900 dark:text-gray-100 transition-colors">Статистика</h2>
        <div className="flex gap-2 mb-4 md:hidden">
          <button onClick={() => setCollapseStats(v => !v)} className="px-3 py-1 rounded bg-blue-100 dark:bg-gray-700 text-blue-900 dark:text-blue-200 font-semibold">{collapseStats ? 'Показать' : 'Скрыть'} метрики</button>
          <button onClick={() => setCollapseCharts(v => !v)} className="px-3 py-1 rounded bg-green-100 dark:bg-gray-700 text-green-900 dark:text-green-200 font-semibold">{collapseCharts ? 'Показать' : 'Скрыть'} графики</button>
        </div>
        {/* Карточки статистики */}
        {!collapseStats && (
          errorStats ? (
            <div className="text-center text-red-500 py-4 animate-fade-in">{errorStats}</div>
          ) : stats ? (
            Object.keys(stats).length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6 mb-8 animate-fade-in">
                {Object.entries(stats).map(([key, value]) => {
                  const statLabels = {
                    total_dialogs: 'Всего диалогов',
                    totalDialogs: 'Всего диалогов',
                    total_scenarios: 'Всего сценариев',
                    totalScenarios: 'Всего сценариев',
                    total_achievements: 'Всего достижений',
                    total_users: 'Всего пользователей',
                    totalUsers: 'Всего пользователей',
                    active_users: 'Активных пользователей',
                    activeUsers: 'Активных пользователей',
                    admins: 'Администраторов',
                    managers: 'Модераторов',
                    completedDialogs: 'Завершённых диалогов',
                    completed_dialogs: 'Завершённых диалогов',
                    users_count: 'Пользователей',
                    dialogs_count: 'Диалогов',
                    // Русские ключи из API
                    'всего_пользователей': 'Всего пользователей',
                    'всего_диалогов': 'Всего диалогов',
                    'завершённых_диалогов': 'Завершённых диалогов',
                    'всего_сценариев': 'Всего сценариев',
                  };
                  return (
                    <div key={key} className="p-4 rounded-xl text-center shadow transition-colors bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
                      <div className="text-gray-700 dark:text-gray-300">{statLabels[key] || key}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-gray-400 dark:text-gray-500 py-8 animate-fade-in">Нет данных для отображения статистики</div>
            )
          ) : (
            <div className="text-center text-gray-400 dark:text-gray-500 py-8 animate-fade-in">Нет данных для отображения статистики</div>
          )
        )}
        {/* Графики */}
        {!collapseCharts && <>
          {/* Bar: топ-5 популярных сценариев */}
          {topScenarios ? (
            <div className="rounded-2xl shadow-xl p-6 mb-6 flex flex-col md:flex-row items-center justify-between gap-8 overflow-x-auto transition-colors bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 animate-fade-in">
              <div className="w-full md:w-2/3 flex-shrink-0">
                <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100 text-center md:text-left">График сценариев</h2>
                <Bar
                  data={{
                    labels: topScenarios.labels,
                    datasets: [{
                      label: 'Количество диалогов',
                      data: topScenarios.counts,
                      backgroundColor: getChartColors().bg1,
                      borderRadius: 8,
                      maxBarThickness: 40,
                    }],
                  }}
                  options={{
                    responsive: true,
                    animation: { duration: 1200, easing: 'easeInOutQuart' },
                    plugins: { legend: { display: false, labels: { color: getChartColors().text } } },
                    indexAxis: 'x',
                    scales: {
                      x: { title: { display: false }, ticks: { color: getChartColors().text, font: { size: 12 } }, grid: { color: getChartColors().grid } },
                      y: { title: { display: true, text: 'Диалогов', color: getChartColors().text }, beginAtZero: true, ticks: { color: getChartColors().text, font: { size: 12 } }, grid: { color: getChartColors().grid } },
                    },
                  }}
                />
              </div>
              <div className="w-full md:w-1/3 flex flex-col items-center md:items-start mt-8 md:mt-0">
                <h3 className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-200 text-center md:text-left">Статистика</h3>
                <div className="w-full flex flex-col gap-2">
                  <div className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">{topScenarios.labels.length} сценариев</div>
                  {topScenarios.labels.map((label, idx) => {
                    const count = topScenarios.counts[idx];
                    const max = Math.max(...topScenarios.counts);
                    const percent = max > 0 ? Math.round((count / max) * 100) : 0;
                    return (
                      <div key={label} className="mb-2">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                          <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">{percent}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                          <div className="bg-blue-500 h-2.5 rounded-full transition-all" style={{ width: `${percent}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-xl shadow transition"
                  onClick={() => setShowScenarioDetailsModal({ show: true, title: 'Детали по сценариям', details: { type: 'scenario', label: 'Все сценарии' } })}
                >
                  Подробнее
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 dark:text-gray-500 py-8 animate-fade-in">Нет данных по сценариям</div>
          )}
          {/* Bar: распределение достижений */}
          {achievementsDist ? (
            <div className="rounded-2xl shadow-xl p-6 mb-6 overflow-x-auto transition-colors bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 animate-fade-in">
              <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Распределение достижений</h2>
              <Bar
                data={{
                  labels: achievementsDist.labels,
                  datasets: [{
                    label: 'Пользователей с достижением',
                    data: achievementsDist.counts,
                    backgroundColor: [getChartColors().bg2, getChartColors().bg3, getChartColors().bg4, getChartColors().bg5, getChartColors().bg6],
                  }],
                }}
                options={{
                  responsive: true,
                  animation: { duration: 1200, easing: 'easeInOutQuart' },
                  plugins: { legend: { display: false, labels: { color: getChartColors().text } } },
                  indexAxis: 'y',
                  scales: {
                    x: { title: { display: true, text: 'Пользователей', color: getChartColors().text }, beginAtZero: true, ticks: { color: getChartColors().text, font: { size: 12 } }, grid: { color: getChartColors().grid } },
                    y: { title: { display: true, text: 'Достижение', color: getChartColors().text }, ticks: { color: getChartColors().text, font: { size: 12 } }, grid: { color: getChartColors().grid } },
                  },
                  onClick: (evt, elements) => {
                    if (elements.length > 0) {
                      const index = elements[0].index;
                      const label = achievementsDist.labels[index];
                      setShowScenarioDetailsModal({ show: true, title: `Достижение: ${label}`, details: { type: 'achievement', label } });
                    }
                  },
                }}
              />
            </div>
          ) : (
            <div className="text-center text-gray-400 dark:text-gray-500 py-8 animate-fade-in">Нет данных по достижениям</div>
          )}
          {/* График активности подчинённых по дням */}
          {orgStats && orgStats.activity_chart && orgStats.activity_chart.datasets?.length > 0 && (
            <div className="rounded-2xl shadow-xl p-6 mb-6 overflow-x-auto transition-colors bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 animate-fade-in">
              <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Активность обучающихся</h2>
              <div className="flex flex-col md:flex-row gap-2 mb-4 items-center">
                <label className="text-gray-700 dark:text-gray-300 text-sm">Период:</label>
                <input type="date" value={activityFrom} onChange={e => setActivityFrom(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <span className="text-gray-500 dark:text-gray-400">—</span>
                <input type="date" value={activityTo} onChange={e => setActivityTo(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <button onClick={() => fetchOrgStats(activityFrom, activityTo)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">Показать</button>
              </div>
              <div className="h-72">
                <Line
                  data={{
                    labels: orgStats.activity_chart.labels.map(d => new Date(d).toLocaleDateString('ru', {day: 'numeric', month: 'short'})),
                    datasets: orgStats.activity_chart.datasets.map((ds, i) => ({
                      label: ds.username,
                      data: ds.data,
                      borderColor: `hsl(${i * 60}, 70%, 50%)`,
                      backgroundColor: `hsla(${i * 60}, 70%, 50%, 0.1)`,
                      tension: 0.3,
                      fill: false,
                      pointRadius: 4,
                      pointHoverRadius: 7
                    }))
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: true, position: 'bottom', labels: { color: getChartColors().text } } },
                    scales: {
                      y: { beginAtZero: true, title: { display: true, text: 'Диалогов', color: getChartColors().text }, ticks: { stepSize: 1, color: getChartColors().text }, grid: { color: getChartColors().grid } },
                      x: { title: { display: true, text: 'Дата', color: getChartColors().text }, ticks: { color: getChartColors().text }, grid: { color: getChartColors().grid } }
                    }
                  }}
                />
              </div>
            </div>
          )}
        </>}
        {/* Модальное окно подробностей по сценариям */}
        {showScenarioDetailsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 w-full max-w-lg mx-2 relative animate-fade-in">
              <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" onClick={() => setShowScenarioDetailsModal(false)}>
                <XMarkIcon className="w-6 h-6" />
              </button>
              <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Детали по сценариям</h3>
              <div className="max-h-[60vh] overflow-y-auto">
                {topScenarios && topScenarios.labels.length > 0 ? (
                  <table className="min-w-full bg-white dark:bg-gray-800 rounded-xl text-xs sm:text-sm transition-colors">
                    <thead>
                      <tr className="text-left text-blue-900 dark:text-blue-200">
                        <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Сценарий</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">Диалогов</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50">% от максимума</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topScenarios.labels.map((label, idx) => {
                        const count = topScenarios.counts[idx];
                        const max = Math.max(...topScenarios.counts);
                        const percent = max > 0 ? Math.round((count / max) * 100) : 0;
                        return (
                          <tr key={label} className="border-b border-gray-200 dark:border-gray-700">
                            <td className="px-3 py-3 font-medium text-gray-900 dark:text-gray-100 border-t border-gray-200 dark:border-gray-700">{label}</td>
                            <td className="px-3 py-3 text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700">{count}</td>
                            <td className="px-3 py-3 text-gray-900 dark:text-gray-100 border-t border-gray-200 dark:border-gray-700">
                              <div className="flex items-center gap-2">
                                <span>{percent}%</span>
                                <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${percent}%` }}></div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-gray-400">Нет данных по сценариям</div>
                )}
              </div>
              <div className="flex justify-end mt-4">
                <button
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  onClick={() => setShowScenarioDetailsModal(false)}
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Модальные окна для CRUD достижений и сценариев, подтверждения удаления */}
      {showAddAchievementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-colors">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-xs sm:max-w-md max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700 transition-colors relative animate-fade-in">
            <div className="flex justify-between items-center mb-2 sm:mb-4">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Добавить новое достижение</h2>
              <button onClick={() => setShowAddAchievementModal(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1 sm:p-2">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAddAchievementSubmit} className="space-y-2 sm:space-y-4">
              <div>
                <label htmlFor="achievement-title" className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">Название:</label>
                <input
                  type="text"
                  id="achievement-title"
                  name="title"
                  value={achievementFormData.title}
                  onChange={handleAddAchievementChange}
                  className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label htmlFor="achievement-description" className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">Описание:</label>
                <textarea
                  id="achievement-description"
                  name="description"
                  value={achievementFormData.description}
                  onChange={handleAddAchievementChange}
                  className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-full py-2 px-3 bg-blue-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 leading-tight focus:outline-none focus:shadow-outline h-32 resize-none transition-colors"
                  required
                ></textarea>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">Иконка:</label>
                <div className="flex gap-2 items-center">
                  {/* ...иконка превью и загрузка... */}
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.gif,.svg"
                    onChange={e => handleIconUpload(e, false)}
                    className="block text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="achievement-points" className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">Баллы:</label>
                <input
                  type="number"
                  id="achievement-points"
                  name="points"
                  value={achievementFormData.points}
                  onChange={handleAddAchievementChange}
                  className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                />
              </div>
              <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  id="achievement-is-repeatable"
                  name="is_repeatable"
                  checked={achievementFormData.is_repeatable}
                  onChange={handleAddAchievementChange}
                  className="mr-2 leading-tight accent-blue-600 dark:accent-blue-400"
                />
                <label htmlFor="achievement-is-repeatable" className="text-gray-700 dark:text-gray-300 text-sm font-bold">Повторяемое</label>
              </div>
              <div className="mb-4">
                <label htmlFor="achievement-requirements-type" className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">Тип требования:</label>
                <select
                  id="achievement-requirements-type"
                  name="requirementType"
                  value={achievementFormData.requirements.type}
                  onChange={handleAddAchievementChange}
                  className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-full py-2 px-3 bg-blue-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 leading-tight focus:outline-none focus:shadow-outline mb-2 transition-colors"
                >
                  <option value="none">Нет</option>
                  <option value="total_dialogs">Завершить диалогов</option>
                  <option value="time">Время (секунд)</option>
                </select>
                {achievementFormData.requirements.type === 'time' ? (
                  <div>
                    <label htmlFor="achievement-requirements-value" className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">Время (секунд):</label>
                    <input
                      type="number"
                      id="achievement-requirements-value"
                      name="requirementValue"
                      value={achievementFormData.requirements.value}
                      onChange={handleAddAchievementChange}
                      className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                      placeholder="Введите время в секундах"
                    />
                  </div>
                ) : achievementFormData.requirements.type !== 'none' ? (
                  <div>
                    <label htmlFor="achievement-requirements-value" className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">Значение:</label>
                    <input
                      type="number"
                      id="achievement-requirements-value"
                      name="requirementValue"
                      value={achievementFormData.requirements.value}
                      onChange={handleAddAchievementChange}
                      className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                      placeholder="Введите числовое значение"
                    />
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddAchievementModal(false);
                    setAchievementFormData({ title: '', description: '', icon: '', points: 0, is_repeatable: false, requirements: { type: 'none', value: '' } });
                  }}
                  className="bg-gray-300 dark:bg-gray-600 hover:bg-gray-700 dark:hover:bg-gray-600 text-black dark:text-gray-100 font-bold py-2 w-full rounded transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 w-full rounded transition-colors"
                >
                  Добавить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showEditAchievementModal && currentAchievement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-xs sm:max-w-md max-h-[90vh] overflow-y-auto relative">
            <div className="flex justify-between items-center mb-2 sm:mb-4">
              <h2 className="text-lg sm:text-2xl font-bold">Редактировать достижение</h2>
              <button onClick={() => setShowEditAchievementModal(false)} className="text-gray-500 hover:text-gray-700 p-1 sm:p-2">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSaveAchievementEdit} className="space-y-2 sm:space-y-4">
              <input type="text" placeholder="Название" value={currentAchievement.title || ''} onChange={e => setCurrentAchievement({...currentAchievement, title: e.target.value})} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600" required />
              <textarea placeholder="Описание" value={currentAchievement.description || ''} onChange={e => setCurrentAchievement({...currentAchievement, description: e.target.value})} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600" rows={2} />
              <input type="number" placeholder="Баллы" value={currentAchievement.points || 0} onChange={e => setCurrentAchievement({...currentAchievement, points: parseInt(e.target.value) || 0})} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600" />
              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">Иконка:</label>
                <div className="flex gap-2 items-center">
                  <div className="w-12 h-12 flex items-center justify-center">
                    {currentAchievement.icon ? (
                      <img 
                        src={currentAchievement.icon} 
                        alt="icon" 
                        className="w-12 h-12 rounded object-contain" 
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center ${currentAchievement.icon ? 'hidden' : 'block'}`}
                    >
                      <span className="text-xl">🏆</span>
                    </div>
                  </div>
                  <input type="file" accept=".png,.jpg,.jpeg,.gif,.svg" onChange={e => handleIconUpload(e, true)} className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={currentAchievement.is_repeatable || false} onChange={e => setCurrentAchievement({...currentAchievement, is_repeatable: e.target.checked})} />
                <label>Повторяемое</label>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                <button type="button" onClick={() => setShowEditAchievementModal(false)} className="bg-gray-300 dark:bg-gray-600 hover:bg-gray-700 dark:hover:bg-gray-600 text-black dark:text-gray-100 font-bold py-2 w-full rounded transition-colors">Отмена</button>
                <button type="submit" className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 w-full rounded transition-colors">Сохранить</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showAddScenarioModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-colors">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-xs sm:max-w-md max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700 transition-colors relative animate-fade-in">
            <div className="flex justify-between items-center mb-2 sm:mb-6">
              <h2 className="panel-title text-gray-900 dark:text-gray-100">Добавить новый сценарий</h2>
              <button onClick={() => setShowAddScenarioModal(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1 sm:p-2 transition-colors">
                <XMarkIcon className="w-7 h-7" />
              </button>
            </div>
            {/* Информация о шаблоне */}
            {orgTemplate ? (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-700">
                <div className="text-sm text-blue-800 dark:text-blue-200 mb-2"><strong>Шаблон:</strong> {orgTemplate.name}</div>
                {orgTemplate.content && (
                  <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono bg-white dark:bg-gray-900 p-3 rounded-lg border max-h-48 overflow-y-auto">{orgTemplate.content}</pre>
                )}
              </div>
            ) : (
              <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/30 rounded-xl border border-orange-200 dark:border-orange-700">
                <div className="text-sm text-orange-800 dark:text-orange-200">⚠️ Шаблон не назначен администратором</div>
              </div>
            )}
            <form onSubmit={handleAddScenarioSubmit} className="space-y-4">
              <div>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">Название:</label>
                <input type="text" value={scenarioFormData.name} onChange={e => setScenarioFormData({ ...scenarioFormData, name: e.target.value })} className="w-full h-12 px-4 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100" required placeholder="Введите название сценария" />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">Описание:</label>
                <textarea value={scenarioFormData.description} onChange={e => setScenarioFormData({ ...scenarioFormData, description: e.target.value })} className="w-full h-24 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none" required placeholder="Введите описание сценария"></textarea>
              </div>
              {/* Динамические переменные из шаблона */}
              {templateVariables.length > 0 && (
                <div className="p-4 bg-purple-50 dark:bg-purple-900/30 rounded-xl border border-purple-200 dark:border-purple-700">
                  <h4 className="font-bold text-sm mb-3 text-purple-800 dark:text-purple-200">Переменные шаблона</h4>
                  <div className="space-y-4">
                    {templateVariables.map(v => (
                      <div key={v.id} className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-600">
                        <label className="block text-sm font-medium text-purple-700 dark:text-purple-300 mb-2">
                          {orgTemplate?.var_labels?.[v.name] || v.name}
                          <code className="text-purple-500 text-xs ml-2">{`{{${v.name}}}`}</code>
                          {v.var_type === 'select' && <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">select</span>}
                        </label>
                        {v.var_type === 'select' ? (
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Варианты выбора (по одному на строку):</label>
                              <textarea
                                value={(scenarioFormData.variable_options || {})[v.name]?.join('\n') || (v.options || []).join('\n')}
                                onChange={e => {
                                  const lines = e.target.value.split('\n');
                                  setScenarioFormData(p => ({ 
                                    ...p, 
                                    variable_options: { ...p.variable_options, [v.name]: lines }
                                  }));
                                }}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 h-24 resize-none text-sm"
                                placeholder="Вариант 1&#10;Вариант 2&#10;Вариант 3"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Выбранное значение:</label>
                              <select
                                value={(scenarioFormData.variable_values || {})[v.name] || ''}
                                onChange={e => setScenarioFormData(p => ({ ...p, variable_values: { ...p.variable_values, [v.name]: e.target.value } }))}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                              >
                                <option value="">Выберите значение</option>
                                {((scenarioFormData.variable_options || {})[v.name] || v.options || []).filter(opt => opt.trim()).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                            </div>
                          </div>
                        ) : v.var_type === 'textarea' ? (
                          <textarea
                            value={(scenarioFormData.variable_values || {})[v.name] || v.default_value || ''}
                            onChange={e => setScenarioFormData(p => ({ ...p, variable_values: { ...p.variable_values, [v.name]: e.target.value } }))}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 h-20 resize-none"
                            placeholder={orgTemplate?.var_labels?.[v.name] || v.name}
                          />
                        ) : (
                          <input
                            type={v.var_type === 'number' ? 'number' : 'text'}
                            value={(scenarioFormData.variable_values || {})[v.name] || v.default_value || ''}
                            onChange={e => setScenarioFormData(p => ({ ...p, variable_values: { ...p.variable_values, [v.name]: e.target.value } }))}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                            placeholder={orgTemplate?.var_labels?.[v.name] || v.name}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Шаблон промпта (только просмотр) */}
              {orgTemplate?.content && (
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <h4 className="font-bold text-sm mb-2 text-gray-700 dark:text-gray-300">Шаблон промпта (только просмотр)</h4>
                  <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono bg-white dark:bg-gray-900 p-3 rounded-lg border max-h-48 overflow-y-auto">{orgTemplate.content}</pre>
                </div>
              )}
              <div className="flex justify-end gap-3 mt-4">
                <button type="button" onClick={() => setShowAddScenarioModal(false)} className="bg-gray-300 dark:bg-gray-600 text-black dark:text-gray-100 font-bold py-2 px-6 rounded-xl">Отмена</button>
                <button type="submit" className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-xl">Добавить</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showEditScenarioModal && currentScenario && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-xs sm:max-w-md max-h-[90vh] overflow-y-auto relative">
            <div className="flex justify-between items-center mb-2 sm:mb-4">
              <h2 className="text-lg sm:text-2xl font-bold">Редактировать сценарий</h2>
              <button onClick={() => setShowEditScenarioModal(false)} className="text-gray-500 hover:text-gray-700 p-1 sm:p-2">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            {/* Информация о шаблоне */}
            {orgTemplate && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-700">
                <div className="text-sm text-blue-800 dark:text-blue-200 mb-2"><strong>Шаблон:</strong> {orgTemplate.name}</div>
                {orgTemplate.content && (
                  <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono bg-white dark:bg-gray-900 p-3 rounded-lg border max-h-48 overflow-y-auto">{orgTemplate.content}</pre>
                )}
              </div>
            )}
            <form onSubmit={handleSaveScenarioEdit} className="space-y-2 sm:space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Название:</label>
                <input type="text" value={currentScenario.name || ''} onChange={e => setCurrentScenario({...currentScenario, name: e.target.value})} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Описание:</label>
                <textarea value={currentScenario.description || ''} onChange={e => setCurrentScenario({...currentScenario, description: e.target.value})} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600" rows={2} />
              </div>
              {/* Динамические переменные из шаблона */}
              {templateVariables.length > 0 && (
                <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-xl border border-purple-200 dark:border-purple-700">
                  <h4 className="font-bold text-sm mb-2 text-purple-800 dark:text-purple-200">Переменные шаблона</h4>
                  <div className="space-y-3">
                    {templateVariables.map(v => (
                      <div key={v.id} className="p-2 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-600">
                        <label className="block text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
                          {orgTemplate?.var_labels?.[v.name] || v.name}
                          {v.var_type === 'select' && <span className="ml-2 text-[10px] bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">select</span>}
                        </label>
                        {v.var_type === 'select' ? (
                          <div className="space-y-2">
                            <div>
                              <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 block">Варианты:</label>
                              <textarea
                                value={(currentScenario.variable_options || {})[v.name]?.join('\n') || (v.options || []).join('\n')}
                                onChange={e => {
                                  const lines = e.target.value.split('\n');
                                  setCurrentScenario(p => ({ 
                                    ...p, 
                                    variable_options: { ...p.variable_options, [v.name]: lines }
                                  }));
                                }}
                                className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 h-20 resize-none text-xs"
                                placeholder="Вариант 1&#10;Вариант 2"
                              />
                            </div>
                            <select
                              value={(currentScenario.variable_values || {})[v.name] || ''}
                              onChange={e => setCurrentScenario(p => ({ ...p, variable_values: { ...p.variable_values, [v.name]: e.target.value } }))}
                              className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                            >
                              <option value="">Выберите</option>
                              {((currentScenario.variable_options || {})[v.name] || v.options || []).filter(opt => opt.trim()).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </div>
                        ) : v.var_type === 'textarea' ? (
                          <textarea
                            value={(currentScenario.variable_values || {})[v.name] || v.default_value || ''}
                            onChange={e => setCurrentScenario(p => ({ ...p, variable_values: { ...p.variable_values, [v.name]: e.target.value } }))}
                            className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 h-16 resize-none text-sm"
                          />
                        ) : (
                          <input
                            type={v.var_type === 'number' ? 'number' : 'text'}
                            value={(currentScenario.variable_values || {})[v.name] || v.default_value || ''}
                            onChange={e => setCurrentScenario(p => ({ ...p, variable_values: { ...p.variable_values, [v.name]: e.target.value } }))}
                            className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Шаблон промпта (только просмотр) */}
              {orgTemplate?.content && (
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <h4 className="font-bold text-sm mb-2 text-gray-700 dark:text-gray-300">Шаблон промпта (только просмотр)</h4>
                  <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono bg-white dark:bg-gray-900 p-2 rounded border max-h-40 overflow-y-auto">{orgTemplate.content}</pre>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                <button type="button" onClick={() => setShowEditScenarioModal(false)} className="bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-black dark:text-gray-100 font-bold py-2 w-full rounded transition-colors">Отмена</button>
                <button type="submit" className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 w-full rounded transition-colors">Сохранить</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-sm">
            <div className="mb-2 sm:mb-4 text-base sm:text-lg font-semibold text-gray-800">{confirmModal.text}</div>
            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <button className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 w-full sm:w-auto rounded" onClick={() => setConfirmModal({ show: false, onConfirm: null, text: '' })}>Отмена</button>
              <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 w-full sm:w-auto rounded" onClick={confirmModal.onConfirm}>Удалить</button>
            </div>
          </div>
        </div>
      )}
      {/* Модалка создания инвайт-ссылки */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Создать инвайт-ссылку</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Макс. использований (пусто = безлимит)
                </label>
                <input
                  type="number"
                  min="1"
                  value={inviteForm.max_uses}
                  onChange={e => setInviteForm(p => ({ ...p, max_uses: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Например: 10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Срок действия (дней, пусто = бессрочно)
                </label>
                <input
                  type="number"
                  min="1"
                  value={inviteForm.expires_days}
                  onChange={e => setInviteForm(p => ({ ...p, expires_days: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Например: 7"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={createInviteLink}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold"
              >
                Создать
              </button>
              <button
                onClick={() => { setShowInviteModal(false); setInviteForm({ max_uses: '', expires_days: '' }); }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white py-2 rounded-lg font-semibold"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Модалка покупки тарифа */}
      {showTariffModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Купить тариф для организации</h3>
            <div className="space-y-3">
              {availableTariffs.filter(t => t.features?.type === 'b2b' && t.is_active !== false).length === 0 ? (
                <p className="text-gray-500">Нет доступных B2B тарифов</p>
              ) : (
                availableTariffs.filter(t => t.features?.type === 'b2b' && t.is_active !== false).map(t => (
                  <div key={t.id} className={`p-4 rounded-xl border-2 cursor-pointer transition ${selectedTariffId === t.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'}`} onClick={() => setSelectedTariffId(t.id)}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-bold text-gray-900 dark:text-white">{t.name}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{t.description}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {t.participant_limit ? `👥 До ${t.participant_limit} участников` : '👥 Без лимита участников'}
                          {' • '}
                          {t.monthly_limit ? `🪙 ${t.monthly_limit.toLocaleString()} токенов/мес` : '🪙 Безлимит токенов'}
                          {' • '}
                          📅 {t.period_days || 30} дней
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-blue-600">{t.price_per_month} ₽</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={async () => {
                  if (!selectedTariffId) { toast.error('Выберите тариф'); return; }
                  const token = localStorage.getItem('access_token');
                  const selectedTariff = availableTariffs.find(t => t.id === selectedTariffId);
                  
                  // Если тариф бесплатный - применяем сразу без оплаты
                  if (selectedTariff && selectedTariff.price_per_month === 0) {
                    try {
                      const res = await fetch('/api/admin/my-organization/tariff', {
                        method: 'PUT',
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ tariff_id: selectedTariffId })
                      });
                      if (res.ok) {
                        toast.success('Тариф успешно применён!');
                        setShowTariffModal(false);
                        setSelectedTariffId(null);
                        fetchOrgStats();
                      } else {
                        const err = await res.json();
                        toast.error(err.error || 'Ошибка применения тарифа');
                      }
                    } catch (e) { toast.error('Ошибка'); }
                    return;
                  }
                  
                  // Платный тариф - создаём платёж
                  try {
                    const res = await fetch('/api/payments/create', {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ tariff_id: selectedTariffId, for_organization: true, return_url: window.location.href })
                    });
                    const data = await res.json();
                    if (data.confirmation_url) {
                      window.location.href = data.confirmation_url;
                    } else {
                      toast.error(data.error || 'Ошибка создания платежа');
                    }
                  } catch (e) { toast.error('Ошибка'); }
                }}
                disabled={!selectedTariffId}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 rounded-lg font-semibold"
              >
                {selectedTariffId && availableTariffs.find(t => t.id === selectedTariffId)?.price_per_month === 0 ? 'Применить' : 'Оплатить'}
              </button>
              <button
                onClick={() => { setShowTariffModal(false); setSelectedTariffId(null); }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white py-2 rounded-lg font-semibold"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModeratorPanel;